import { BuyInvoiceDataType } from "@/api/BuyInvoiceApi";
import { ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import { ProviderRecibeDataType } from "@/api/ProviderRecibeApi";
import { PROVIDER_RECIBE_MOTIVO_ABONO_PREFIX, PROVIDER_RECIBE_MOTIVO_ABONO_SEPARATOR, PROVIDER_RECIBE_MOTIVO_PREFIX } from "@utils/Constants";

export const MOTIVO_PREFIX = PROVIDER_RECIBE_MOTIVO_PREFIX;

export type ParsedMotivoReferences = {
  fecNumeros: number[];
  providerInvoiceNumeros: string[];
};

export type RepairPlanStatus = "ready" | "skipped" | "conflict" | "error";

export type RepairPlanDocument = {
  kind: "buyInvoice" | "providerInvoice";
  id: string;
  label: string;
  providerId?: string;
  alreadyLinked: boolean;
};

export type RepairPlan = {
  recibeId: string;
  numeroRecibo?: number;
  motivo?: string;
  status: RepairPlanStatus;
  message: string;
  proposedProviderId?: string;
  proposedProviderName?: string;
  documents: RepairPlanDocument[];
  missingReferences: string[];
};

export type RepairBatchSummary = {
  repaired: string[];
  skipped: string[];
  failed: Array<{ recibeId: string; message: string }>;
};

export function parseMotivoReferences(motivo?: string | null): ParsedMotivoReferences {
  const fecNumeros: number[] = [];
  const providerInvoiceNumeros: string[] = [];

  if (!motivo?.trim()) {
    return { fecNumeros, providerInvoiceNumeros };
  }

  let body = motivo.trim();

  // El detalle del abono parcial va despues del separador y NO se liga al recibo:
  // esa factura queda pendiente a proposito, asi que se descarta antes de tokenizar.
  const abonoSeparatorIndex = body.indexOf(PROVIDER_RECIBE_MOTIVO_ABONO_SEPARATOR);
  if (abonoSeparatorIndex >= 0) {
    body = body.slice(0, abonoSeparatorIndex).trim();
  }

  // Un recibo que solo llevo un abono no tiene facturas que ligar.
  if (body.toLowerCase().startsWith(PROVIDER_RECIBE_MOTIVO_ABONO_PREFIX.toLowerCase())) {
    return { fecNumeros, providerInvoiceNumeros };
  }

  if (body.toLowerCase().startsWith(PROVIDER_RECIBE_MOTIVO_PREFIX.trim().toLowerCase())) {
    body = body.slice(PROVIDER_RECIBE_MOTIVO_PREFIX.trim().length).trim();
  }

  if (!body) {
    return { fecNumeros, providerInvoiceNumeros };
  }

  const tokens = body.split(" , ").map((token) => token.trim()).filter(Boolean);

  for (const token of tokens) {
    const fecMatch = /^FEC-(\d+)$/i.exec(token);
    if (fecMatch) {
      const numero = parseInt(fecMatch[1], 10);
      if (!Number.isNaN(numero) && !fecNumeros.includes(numero)) {
        fecNumeros.push(numero);
      }
      continue;
    }

    if (!providerInvoiceNumeros.includes(token)) {
      providerInvoiceNumeros.push(token);
    }
  }

  return { fecNumeros, providerInvoiceNumeros };
}

export function isRepairCandidate(
  recibe: ProviderRecibeDataType,
  parsed: ParsedMotivoReferences,
  linkedBuyCount: number,
  linkedProviderCount: number,
): boolean {
  if (recibe.anulado) {
    return false;
  }

  const expectedLinks = parsed.fecNumeros.length + parsed.providerInvoiceNumeros.length;
  if (expectedLinks === 0) {
    return false;
  }

  if (!recibe.providerId) {
    return true;
  }

  const actualLinks = linkedBuyCount + linkedProviderCount;
  return actualLinks < expectedLinks;
}

type BuildRepairPlanInput = {
  recibe: ProviderRecibeDataType;
  parsed: ParsedMotivoReferences;
  buyInvoicesByNumero: Map<number, BuyInvoiceDataType>;
  providerInvoicesByNumero: Map<string, ProviderInvoiceDataType>;
  linkedBuyInvoices: BuyInvoiceDataType[];
  linkedProviderInvoices: ProviderInvoiceDataType[];
  providerNamesById?: Map<string, string>;
};

export function buildRepairPlan(input: BuildRepairPlanInput): RepairPlan {
  const {
    recibe,
    parsed,
    buyInvoicesByNumero,
    providerInvoicesByNumero,
    linkedBuyInvoices,
    linkedProviderInvoices,
    providerNamesById = new Map(),
  } = input;

  const recibeId = `${recibe.id}`;
  const base: RepairPlan = {
    recibeId,
    numeroRecibo: recibe.numero_recibo,
    motivo: recibe.motivo,
    status: "skipped",
    message: "",
    documents: [],
    missingReferences: [],
  };

  const totalRefs = parsed.fecNumeros.length + parsed.providerInvoiceNumeros.length;
  if (totalRefs === 0) {
    return { ...base, message: "Sin referencias en el motivo" };
  }

  const documents: RepairPlanDocument[] = [];
  const missingReferences: string[] = [];
  const conflicts: string[] = [];
  const errors: string[] = [];
  const providerIds = new Set<string>();

  if (recibe.providerId) {
    providerIds.add(`${recibe.providerId}`);
  }

  for (const numero of parsed.fecNumeros) {
    const label = `FEC-${numero}`;
    const invoice = buyInvoicesByNumero.get(numero);

    if (!invoice?.id) {
      missingReferences.push(label);
      errors.push(`No se encontró ${label}`);
      continue;
    }

    const providerId = invoice.providerId ? `${invoice.providerId}` : undefined;
    if (providerId) {
      providerIds.add(providerId);
    }

    const linkedRecibeId = invoice.providerRecibeId ? `${invoice.providerRecibeId}` : undefined;
    if (linkedRecibeId && linkedRecibeId !== recibeId) {
      conflicts.push(`${label} ya está ligada a otro recibo`);
      continue;
    }

    documents.push({
      kind: "buyInvoice",
      id: `${invoice.id}`,
      label,
      providerId,
      alreadyLinked: linkedRecibeId === recibeId,
    });
  }

  for (const numero of parsed.providerInvoiceNumeros) {
    const invoice = providerInvoicesByNumero.get(numero);

    if (!invoice?.id) {
      missingReferences.push(numero);
      errors.push(`No se encontró factura de proveedor ${numero}`);
      continue;
    }

    const providerId = invoice.providerId ? `${invoice.providerId}` : undefined;
    if (providerId) {
      providerIds.add(providerId);
    }

    const linkedRecibeId = invoice.providerRecibeId ? `${invoice.providerRecibeId}` : undefined;
    if (linkedRecibeId && linkedRecibeId !== recibeId) {
      conflicts.push(`Factura ${numero} ya está ligada a otro recibo`);
      continue;
    }

    documents.push({
      kind: "providerInvoice",
      id: `${invoice.id}`,
      label: numero,
      providerId,
      alreadyLinked: linkedRecibeId === recibeId,
    });
  }

  if (errors.length > 0) {
    return {
      ...base,
      status: "error",
      message: errors.join("; "),
      documents,
      missingReferences,
    };
  }

  if (conflicts.length > 0) {
    return {
      ...base,
      status: "conflict",
      message: conflicts.join("; "),
      documents,
      missingReferences,
    };
  }

  const distinctProviderIds = [...providerIds].filter(Boolean);
  if (distinctProviderIds.length > 1) {
    return {
      ...base,
      status: "conflict",
      message: "Las facturas del motivo pertenecen a distintos proveedores",
      documents,
      missingReferences,
    };
  }

  const proposedProviderId = distinctProviderIds[0];
  const needsProviderUpdate = !recibe.providerId && !!proposedProviderId;
  const documentsNeedingLink = documents.filter((document) => !document.alreadyLinked);
  const expectedLinkedCount = parsed.fecNumeros.length + parsed.providerInvoiceNumeros.length;
  const actualLinkedCount = linkedBuyInvoices.length + linkedProviderInvoices.length;

  if (!needsProviderUpdate && documentsNeedingLink.length === 0 && recibe.providerId) {
    if (actualLinkedCount >= expectedLinkedCount) {
      return {
        ...base,
        status: "skipped",
        message: "El recibo ya está completo",
        proposedProviderId: recibe.providerId,
        proposedProviderName: providerNamesById.get(`${recibe.providerId}`),
        documents,
        missingReferences,
      };
    }
  }

  if (!proposedProviderId) {
    return {
      ...base,
      status: "error",
      message: "No fue posible determinar el proveedor desde las facturas referenciadas",
      documents,
      missingReferences,
    };
  }

  if (documentsNeedingLink.length === 0 && !needsProviderUpdate) {
    return {
      ...base,
      status: "skipped",
      message: "No hay cambios pendientes",
      proposedProviderId,
      proposedProviderName: providerNamesById.get(proposedProviderId),
      documents,
      missingReferences,
    };
  }

  return {
    ...base,
    status: "ready",
    message: "Listo para reparar",
    proposedProviderId,
    proposedProviderName: providerNamesById.get(proposedProviderId),
    documents,
    missingReferences,
  };
}

export function formatRepairDocumentsSummary(documents: RepairPlanDocument[]): string {
  if (documents.length === 0) {
    return "";
  }
  return documents.map((document) => document.label).join(" , ");
}
